/**
 * Floating tooltip shown when the cursor hovers over a measurement.
 * Renders inline in the viewer overlay (NOT a portal), so it inherits
 * the same stacking context as the Toolbar/Messungen panels.
 */

import type { Measurement } from '../viewer/measurements'
import { colorForUser, initialsFor } from '../viewer/annotations'

interface Props {
  measurement: Measurement
  x: number
  y: number
}

export function MeasurementTooltip({ measurement, x, y }: Props) {
  const author = measurement.createdBy
  const created = measurement.createdAt
  const initials = initialsFor(author)
  const color = author ? colorForUser(author.id) : 0x888888

  // Display name: prefer "First Last", fall back to email. Show the email
  // on a second line ONLY when we actually have a real name above it —
  // otherwise it would just be repeated verbatim.
  const fullName = author
    ? `${author.first_name ?? ''} ${author.last_name ?? ''}`.trim()
    : ''
  const primary = fullName || author?.email || 'Unbekannt'
  const secondary = fullName && author?.email ? author.email : null

  // Position the tooltip slightly right + below cursor; clamp to viewport.
  const left = Math.min(x + 14, window.innerWidth - 260)
  const top = Math.min(y + 14, window.innerHeight - 110)

  return (
    <div
      style={{
        position: 'fixed',
        left,
        top,
        zIndex: 50,
        pointerEvents: 'none',
        background: 'rgba(20,22,26,0.95)',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 8,
        boxShadow: '0 6px 22px rgba(0,0,0,0.55)',
        padding: '8px 10px',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: 12,
        color: '#e7e9ec',
        maxWidth: 260,
        backdropFilter: 'blur(6px) saturate(140%)',
        WebkitBackdropFilter: 'blur(6px) saturate(140%)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: '#' + color.toString(16).padStart(6, '0'),
            color: '#fff',
            fontWeight: 700,
            fontSize: 10,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {initials}
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {primary}
          </div>
          {secondary && (
            <div
              style={{
                opacity: 0.65,
                fontSize: 11,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {secondary}
            </div>
          )}
        </div>
      </div>
      <div style={{ marginTop: 6, opacity: 0.85, fontSize: 12 }}>
        {measurement.label}
      </div>
      {created && (
        <div style={{ marginTop: 2, opacity: 0.6, fontSize: 11 }}>
          {formatDate(created)}
        </div>
      )}
    </div>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
